import type { DeployRecord, WorkPlan } from "@botallion/contracts";
import type { FeedLine } from "../describe.ts";
import { html, link, type Html } from "../html.ts";
import { currentPlan, type DashboardState } from "../state.ts";
import { clock, shortSha, type ViewContext } from "./common.ts";

/** Plans still waiting for a human. */
export function pendingPlans(state: DashboardState): WorkPlan[] {
  return Object.values(state.plans).filter((p) => p.approvedBy === null).map((p) => currentPlan(state, p));
}

/** Succeeded previews nobody has approved for promotion. */
export function pendingPromotions(state: DashboardState): DeployRecord[] {
  return Object.values(state.deploys).filter((d) => d.kind === "preview" && d.status === "succeeded" && d.approvedBy === null);
}

/** The answers that will be sent with an approval: saved answers, overridden by what the approver typed. */
export function answersFor(ctx: Pick<ViewContext, "answers">, plan: WorkPlan): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of plan.questions) {
    const typed = ctx.answers[`${plan.specId}/${q.id}`]?.trim();
    if (typed) out[q.id] = typed;
  }
  return out;
}

/** Blocking questions with no answer, saved or typed. Approval is refused until there are none. */
export function unansweredBlocking(ctx: Pick<ViewContext, "answers">, plan: WorkPlan): string[] {
  const typed = answersFor(ctx, plan);
  return plan.questions.filter((q) => q.blocking && !q.answer && !typed[q.id]).map((q) => q.id);
}

function button(ctx: ViewContext, action: string, attrs: Html, label: string, blockedBy: string | null): Html {
  const busy = ctx.busy.has(action);
  const why = blockedBy ?? ctx.approver.why;
  const disabled = busy || !ctx.approver.canApprove || blockedBy !== null;
  return html`<button class="approve" ${attrs}${disabled ? " disabled" : ""}>${busy ? "Sending..." : label}</button>${why && !busy ? html`<span class="why">${why}</span>` : ""}`;
}

/** View 7: the two approval buttons, shown whenever something waits on a person. */
export function approvalsView(ctx: ViewContext): Html {
  const { state } = ctx;
  const plans = pendingPlans(state);
  const promotions = pendingPromotions(state);
  if (!plans.length && !promotions.length) return html``;
  return html`<div class="approvals">
    ${plans.map((plan) => {
      const missing = unansweredBlocking(ctx, plan);
      return html`<div class="approval plan">
        <div class="what"><strong>Work plan waiting for approval:</strong> ${plan.title} <span class="muted">(${plan.packages.length} packages: ${plan.packages.map((p) => p.id).join(", ")})</span></div>
        ${plan.questions.length
          ? html`<div class="answers">${plan.questions.map((q) => html`<label>${q.blocking ? html`<strong>Blocking</strong> ` : ""}${q.id}: ${q.text}
              <textarea rows="1" data-answer="${plan.specId}/${q.id}" placeholder="${q.answer ?? "Your answer"}">${ctx.answers[`${plan.specId}/${q.id}`] ?? ""}</textarea></label>`)}</div>`
          : ""}
        <div class="actions">${button(ctx, `plan:${plan.specId}`, html`data-action="approve-plan" data-spec="${plan.specId}"`, "Approve and start", missing.length ? `Answer ${missing.join(", ")} first.` : null)}</div>
      </div>`;
    })}
    ${promotions.map((d) => html`<div class="approval promotion">
      <div class="what"><strong>Preview ready for promotion:</strong> ${d.stack} <code>${d.branch}</code> at <code>${shortSha(d.commit)}</code>, deployed ${clock(d.finishedAt)}
        ${d.urls.map((u) => html` ${link(u.url, u.worker)}`)}</div>
      <div class="actions">${button(ctx, `deploy:${d.id}`, html`data-action="approve-promotion" data-deploy="${d.id}"`, "Approve promotion", null)}</div>
    </div>`)}
  </div>`;
}

/** The activity feed, newest first. */
export function feedView(lines: readonly FeedLine[]): Html {
  if (!lines.length) return html`<p class="empty">Nothing has happened yet. Activity appears here once an agent joins.</p>`;
  return html`<ol class="feed">${lines.map((l) => html`<li class="${l.tone}"><span class="t">${clock(l.ts)}</span> ${l.text}</li>`)}</ol>`;
}
